// The backend records a denied approval by writing a sentinel into the run's
// output: "[Approval denied]", or "[Approval denied: <reason>]" when a reason
// was given (auto-deny always sets one). It is a marker for the UI to read,
// not text to show: the "Denied" badge already says it, and it is English.
//
// The marker is not always at the end. On resume the output is prefixed by
// what earlier steps produced, and the runner can append a "⚠ Warning: ..."
// note after it (workflow_runner.py), so text on both sides is kept.
//
// The reason goes into the marker verbatim (graph_builder.py), so it can itself
// contain "]". The backend always ends the marker at a line end, so a reason
// runs to the first "]" that ends its line; only when none does is the first
// "]" taken. Whitespace right after the marker goes with it.
const DENIAL_MARKER =
  /\[Approval denied(?:\]|:[ \t]*([^\n]*?)\](?=[ \t]*(?:\r?\n|$))|:[ \t]*([^\]]*)\])\s*/g

export type DenialSplit = {
  /** The output with every marker removed; empty when nothing else was there. */
  output: string
  /** Whether the output carried a denial marker at all. */
  denied: boolean
  /** Non-empty reasons, in order (a flow can deny more than one approval). */
  reasons: string[]
}

export function splitDenialMarker(output: string | null | undefined): DenialSplit {
  const text = output ?? ""
  const reasons: string[] = []
  let markers = 0
  const stripped = text.replace(
    DENIAL_MARKER,
    (_marker, lineReason: string | undefined, shortReason: string | undefined) => {
      markers += 1
      const reason = (lineReason ?? shortReason)?.trim()
      if (reason) reasons.push(reason)
      return ""
    }
  )
  if (markers === 0) return { output: text, denied: false, reasons }
  return { output: stripped.trimEnd(), denied: true, reasons }
}

/**
 * The denial as a view should show it. Only a run that finished can have been
 * denied -- a failed or still-running run's output is whatever happened before
 * -- so `denied` and `reasons` follow the status, while the marker is stripped
 * from the output whatever the status, since it is never meant to be shown.
 */
export function getRunDenial(run: { status: string; output?: string | null }): DenialSplit {
  const split = splitDenialMarker(run.output)
  const denied = split.denied && run.status === "succeeded"
  return { output: split.output, denied, reasons: denied ? split.reasons : [] }
}
